import { useCallback, useEffect, useRef } from 'react';
import { api } from '../services/api';

type AdEventType =
  | 'adsense_impression'
  | 'adsense_blocked'
  | 'sponsored_impression'
  | 'sponsored_click'
  | 'sitestrip_click';

interface AdEvent {
  type: AdEventType;
  slotId?: string;
  adId?: string;
  timestamp: number;
}

const FLUSH_INTERVAL_MS = 30_000;
const MAX_BATCH_SIZE = 50;

/** Singleton buffer shared across all hook instances */
const eventBuffer: AdEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let subscriberCount = 0;

function flushEvents() {
  if (eventBuffer.length === 0) return;

  const batch = eventBuffer.splice(0, MAX_BATCH_SIZE);

  // Send to existing endpoints — fire-and-forget
  for (const event of batch) {
    switch (event.type) {
      case 'sponsored_impression':
        if (event.adId) api.post(`/ads/${event.adId}/impression`).catch(() => {});
        break;
      case 'sponsored_click':
        if (event.adId) api.post(`/ads/${event.adId}/click`).catch(() => {});
        break;
      case 'sitestrip_click':
        if (event.adId) api.post(`/sitestrips/${event.adId}/click`).catch(() => {});
        break;
      // AdSense events are tracked client-side only (no backend endpoint needed)
      default:
        break;
    }
  }

  // If still events remaining, flush again
  if (eventBuffer.length > 0) flushEvents();
}

function startFlushTimer() {
  if (flushTimer) return;
  flushTimer = setInterval(flushEvents, FLUSH_INTERVAL_MS);
}

function stopFlushTimer() {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
}

export interface AdAnalyticsStats {
  impressions: number;
  blocked: number;
  clicks: number;
  blockRate: number;
  ctr: number;
}

/**
 * Hook for tracking ad analytics client-side.
 * Events are buffered and periodically flushed to the API.
 *
 * Returns tracking functions and a snapshot of current session stats.
 */
export function useAdAnalytics() {
  const statsRef = useRef({ impressions: 0, blocked: 0, clicks: 0 });

  useEffect(() => {
    subscriberCount++;
    startFlushTimer();
    return () => {
      subscriberCount--;
      if (subscriberCount <= 0) {
        flushEvents();
        stopFlushTimer();
      }
    };
  }, []);

  // Flush on page hide (user navigating away)
  useEffect(() => {
    const handler = () => flushEvents();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') handler();
    });
    window.addEventListener('pagehide', handler);
    return () => {
      document.removeEventListener('visibilitychange', handler);
      window.removeEventListener('pagehide', handler);
    };
  }, []);

  const trackImpression = useCallback((slotId: string) => {
    statsRef.current.impressions++;
    eventBuffer.push({ type: 'adsense_impression', slotId, timestamp: Date.now() });
  }, []);

  const trackBlocked = useCallback((slotId: string) => {
    statsRef.current.blocked++;
    eventBuffer.push({ type: 'adsense_blocked', slotId, timestamp: Date.now() });
  }, []);

  const trackSponsoredImpression = useCallback((adId: string) => {
    statsRef.current.impressions++;
    eventBuffer.push({ type: 'sponsored_impression', adId, timestamp: Date.now() });
  }, []);

  const trackClick = useCallback(
    (adId: string, type: 'sponsored_click' | 'sitestrip_click' = 'sponsored_click') => {
      statsRef.current.clicks++;
      eventBuffer.push({ type, adId, timestamp: Date.now() });
    },
    []
  );

  const getStats = useCallback((): AdAnalyticsStats => {
    const { impressions, blocked, clicks } = statsRef.current;
    return {
      impressions,
      blocked,
      clicks,
      blockRate: impressions > 0 ? blocked / (impressions + blocked) : 0,
      ctr: impressions > 0 ? clicks / impressions : 0,
    };
  }, []);

  return { trackImpression, trackBlocked, trackSponsoredImpression, trackClick, getStats };
}
